/**
 * Event delegation system
 *
 * Centralized click handler for all data-action elements.
 *
 * @module core/events
 */

export function setupEventDelegation(deps) {
    const backdropPointerStarts = new WeakSet();

    document.addEventListener('pointerdown', (event) => {
        document.querySelectorAll('[data-action="closeModalOnBackdrop"]').forEach((modal) => {
            backdropPointerStarts.delete(modal);
        });

        const backdrop = event.target.closest('[data-action="closeModalOnBackdrop"]');
        if (backdrop && event.target === backdrop) {
            backdropPointerStarts.add(backdrop);
        }
    }, true);

    document.addEventListener('click', (event) => {
        const target = event.target.closest('[data-action]');
        if (!target) return;

        const action = target.dataset.action;
        const param = target.dataset.param;
        const param2 = target.dataset.param2;

        // Action map - all functions that were previously in onclick handlers
        const actions = {
            // Theme & UI
            'toggleTheme': () => deps.toggleTheme(),
            'showCalendarView': () => deps.showCalendarView(),
            'toggleKanbanSettings': () => deps.toggleKanbanSettings(event),

            // Modals
            'openProjectModal': () => deps.openProjectModal(),
            'openTaskModal': () => deps.openTaskModal(),
            'openSettingsModal': () => { deps.closeUserDropdown(); deps.openSettingsModal(); },
            'switchSettingsTab': () => deps.switchSettingsTab(param),
            'settingsMobileBack': () => deps.settingsMobileBack(),
            'openTaskModalForProject': () => deps.openTaskModalForProject(parseInt(param)),
            'openTaskModalForStatus': () => deps.openTaskModalForStatus(param),
            'openSelectedProjectFromTask': () => deps.openSelectedProjectFromTask(),
            'closeModal': () => deps.closeModal(param),
            'closeTaskModal': () => deps.closeTaskModal(),
            'closeConfirmModal': () => deps.closeConfirmModal(),
            'closeFeedbackDeleteModal': () => deps.closeFeedbackDeleteModal(),
            'closeProjectConfirmModal': () => deps.closeProjectConfirmModal(),
            'closeUnsavedChangesModal': () => deps.closeUnsavedChangesModal(),
            'closeDayItemsModal': () => deps.closeDayItemsModal(),
            'closeDayItemsModalOnBackdrop': () => deps.closeDayItemsModalOnBackdrop(event),
            'openDeleteAccountModal': () => deps.openDeleteAccountModal(),
            'closeDeleteAccountModal': () => deps.closeDeleteAccountModal(),
            'confirmDeleteAccount': () => deps.confirmDeleteAccount(),
            'closeDocsDeleteModal': () => deps.closeDocsDeleteModal(),
            'confirmDocsDeleteModal': () => deps.confirmDocsDeleteModal(),
            'closeDocsDuplicateModal': () => deps.closeDocsDuplicateModal(),
            'confirmDuplicateDocPage': () => deps.confirmDuplicateDocPage(),
            'openDocsVideoModal': () => deps.openDocsVideoModal(),
            'closeDocsVideoModal': () => deps.closeDocsVideoModal(),
            'confirmDocsVideoModal': () => deps.confirmDocsVideoModal(),
            'closeDocsStatusModal': () => deps.closeDocsStatusModal(),
            'confirmDocsStatusModal': () => deps.confirmDocsStatusModal(),
            'openAskKaiModal': () => deps.openAskKaiModal(),
            'submitAskKaiRequest': () => deps.submitAskKaiRequest(),
            'confirmAskKaiProposal': () => deps.confirmAskKaiProposal(),
            'cancelAskKaiModal': () => deps.cancelAskKaiModal(),
            'refineKaiQuery': () => deps.refineKaiQuery(),
            'clearKaiQuery': () => deps.clearKaiQuery(),
            'saveAskKaiPrompt': () => deps.saveAskKaiPrompt(),
            'toggleAskKaiSavedPrompts': () => deps.toggleAskKaiSavedPrompts(),
            'toggleAskKaiSavedForCurrentPrompt': () => deps.toggleAskKaiSavedForCurrentPrompt(),
            'applyAskKaiSavedPrompt': () => deps.applyAskKaiSavedPrompt(param),
            'editAskKaiSavedPromptName': () => deps.editAskKaiSavedPromptName(param),
            'cancelEditAskKaiSavedPromptName': () => deps.cancelEditAskKaiSavedPromptName(),
            'saveAskKaiSavedPromptName': () => deps.saveAskKaiSavedPromptName(param),
            'deleteAskKaiSavedPrompt': () => deps.deleteAskKaiSavedPrompt(param),
            'cancelDeleteAskKaiSavedPrompt': () => deps.cancelDeleteAskKaiSavedPrompt(),
            'confirmDeleteAskKaiSavedPrompt': () => deps.confirmDeleteAskKaiSavedPrompt(param),

            // Dashboard
            'dashKaiSuggest': () => deps.dashKaiSuggest(param),
            'dashFilterAttention': () => deps.dashFilterAttention(param),
            'dashCompleteTask': () => deps.dashCompleteTask(param),
            'dashOpenTasks': () => deps.dashOpenTasks(),
            'dashOpenProjects': () => deps.dashOpenProjects(),

            // Task operations
            'openTaskDetails': () => {
                event.preventDefault();
                if (target.dataset.stopPropagation) event.stopPropagation();
                const taskId = parseInt(param);

                // Check if we're opening from project details page
                const projectTasksList = target.closest('#project-tasks-list');
                if (projectTasksList) {
                    // We're in project details - build navigation context
                    const projectDetailsPage = document.getElementById('project-details');
                    if (projectDetailsPage && projectDetailsPage.classList.contains('active')) {
                        // Get all task items in order
                        const taskItems = Array.from(projectTasksList.querySelectorAll('.project-task-item[data-param]'));
                        const taskIds = taskItems.map(item => parseInt(item.dataset.param));
                        const currentIndex = taskIds.indexOf(taskId);

                        if (currentIndex !== -1 && taskIds.length > 1) {
                            // Find project ID from URL
                            const hash = window.location.hash;
                            const projectIdMatch = hash.match(/project-(\d+)/);
                            const projectId = projectIdMatch ? parseInt(projectIdMatch[1]) : null;

                            const navContext = {
                                projectId,
                                taskIds,
                                currentIndex
                            };
                            deps.openTaskDetails(taskId, navContext);
                            return;
                        }
                    }
                }

                // Check if we're opening from expanded project card on Projects page
                const expandedTaskItem = target.closest('.expanded-task-item');
                if (expandedTaskItem) {
                    // Try mobile structure first (.project-card-mobile)
                    let projectCard = expandedTaskItem.closest('.project-card-mobile');
                    let projectId = null;

                    if (projectCard) {
                        projectId = parseInt(projectCard.dataset.projectId);
                    } else {
                        // Try desktop structure (.pc-item or legacy .project-list-item)
                        const projectListItem = expandedTaskItem.closest('.pc-item, .project-list-item');
                        if (projectListItem && projectListItem.id) {
                            // Extract project ID from id="project-item-123"
                            const match = projectListItem.id.match(/project-item-(\d+)/);
                            if (match) {
                                projectId = parseInt(match[1]);
                            }
                        }
                    }

                    if (projectId) {
                        // Get all task items in this expanded project in order
                        const taskContainer = expandedTaskItem.closest('.expanded-tasks-container');
                        if (taskContainer) {
                            const taskItems = Array.from(taskContainer.querySelectorAll('.expanded-task-item[data-param]'));
                            const taskIds = taskItems.map(item => parseInt(item.dataset.param));
                            const currentIndex = taskIds.indexOf(taskId);

                            if (currentIndex !== -1 && taskIds.length > 1) {
                                const navContext = {
                                    projectId,
                                    taskIds,
                                    currentIndex
                                };
                                deps.openTaskDetails(taskId, navContext);
                                return;
                            }
                        }
                    }
                }

                // Default: open without navigation context
                deps.openTaskDetails(taskId);
            },
            'deleteTask': () => deps.deleteTask(),
            'duplicateTask': () => deps.duplicateTask(),
            'copyTaskLink': () => deps.copyTaskLink(),
            'confirmDelete': () => deps.confirmDelete(),

            // Automations / recurring tasks
            'openRecurrenceModal': () => deps.openRecurrenceModal(param ? parseInt(param) : null),
            'closeRecurrenceModal': () => deps.closeRecurrenceModal(),
            'submitRecurrenceModal': () => deps.submitRecurrenceModal(),
            'selectRecurrencePattern': () => deps.selectRecurrencePattern(param),
            'toggleRecurrenceWeekday': () => deps.toggleRecurrenceWeekday(parseInt(param)),
            'toggleAutomationActive': () => deps.toggleAutomationActive(parseInt(param)),
            'requestDeleteAutomation': () => deps.requestDeleteAutomation(parseInt(param)),
            'cancelDeleteAutomation': () => deps.cancelDeleteAutomation(),
            'deleteAutomation': () => deps.deleteAutomation(parseInt(param)),
            'editAutomation': () => deps.editAutomation(parseInt(param)),
            'openAutomationAudit': () => deps.openAutomationAudit(parseInt(param)),
            'openAutomationAuditTask': () => deps.openAutomationAuditTask(parseInt(param)),
            'toggleAutomationTypePicker': () => deps.toggleAutomationTypePicker(param),
            'selectAutomationType': () => deps.selectAutomationType(param),
            'openAutoArchiveModal': () => deps.openAutoArchiveModal(param ? parseInt(param) : null),
            'closeAutoArchiveModal': () => deps.closeAutoArchiveModal(),
            'submitAutoArchiveModal': () => deps.submitAutoArchiveModal(),
            'selectAutoArchiveMode': () => deps.selectAutoArchiveMode(param),
            'selectAutoArchivePattern': () => deps.selectAutoArchivePattern(param),
            'toggleAutoArchiveDay': () => deps.toggleAutoArchiveWeekday(param),
            'startRecurrenceNewTemplate': () => deps.startRecurrenceNewTemplate(),
            'selectRecurrenceComboboxTask': () => deps.selectRecurrenceComboboxTask(parseInt(param)),
            'clearRecurrenceCombobox': () => deps.clearRecurrenceCombobox(),

            // Project operations
            'showProjectDetails': () => {
                event.preventDefault();
                if (target.dataset.stopPropagation) event.stopPropagation();
                if (target.dataset.referrer === 'calendar') {
                    deps.showProjectDetails(parseInt(param), 'calendar', {
                        month: deps.getCurrentMonth ? deps.getCurrentMonth() : new Date().getMonth(),
                        year:  deps.getCurrentYear  ? deps.getCurrentYear()  : new Date().getFullYear(),
                    });
                    return;
                }
                const isDashboard = document.getElementById('dashboard').classList.contains('active');
                const referrer = isDashboard ? 'dashboard' : 'projects';
                deps.showProjectDetails(parseInt(param), referrer);
            },
            'toggleProjectExpand': () => deps.toggleProjectExpand(parseInt(param)),
            'expandAllProjects': () => deps.expandAllProjects(),
            'collapseAllProjects': () => deps.collapseAllProjects(),
            'toggleProjectMenu': () => deps.toggleProjectMenu(event),
            'editProjectTitle': () => deps.editProjectTitle(parseInt(param), param2),
            'saveProjectTitle': () => deps.saveProjectTitle(parseInt(param)),
            'cancelProjectTitle': () => deps.cancelProjectTitle(),
            'handleDeleteProject': () => deps.handleDeleteProject(parseInt(param)),
            'resetProjectTaskOrder': () => deps.resetProjectTaskOrder(parseInt(param)),
            'setProjectTaskGroupBy': () => deps.setProjectTaskGroupBy(parseInt(param), param2),
            'toggleProjectTaskGroup': () => deps.toggleProjectTaskGroup(param),
            'resetListViewTaskOrder': () => deps.resetListViewTaskOrder(parseInt(param)),
            'resetExpandedProjectTaskOrder': () => deps.resetExpandedProjectTaskOrder(parseInt(param)),
            'handleDuplicateProject': () => deps.handleDuplicateProject(parseInt(param)),
            'toggleProjectColorPicker': () => deps.toggleProjectColorPicker(parseInt(param)),
            'updateProjectColor': () => deps.updateProjectColor(parseInt(param), param2),
            'selectNewProjectColor': () => deps.selectNewProjectColor(param),
            'toggleNewProjectColorPicker': () => deps.toggleNewProjectColorPicker(),
            'toggleProjectTemplateSection': () => deps.toggleProjectTemplateSection(),
            'openCustomProjectColorPicker': () => deps.openCustomProjectColorPicker(parseInt(param)),
            'navigateToProjectStatus': () => deps.navigateToProjectStatus(parseInt(param), param2),
            'navigateToProjectTasksList': () => deps.navigateToProjectTasksList(parseInt(param)),
            'setProjectsView': () => deps.setProjectsView(param),
            'deleteProject': () => deps.deleteProject(),
            'confirmProjectDelete': () => deps.confirmProjectDelete(),
            'closeDuplicateProjectModal': () => deps.closeDuplicateProjectModal(),
            'confirmDuplicateProject': () => deps.confirmDuplicateProject(),
            'openSaveAsTemplateModal': () => deps.openSaveAsTemplateModal(parseInt(param)),
            'closeSaveAsTemplateModal': () => deps.closeSaveAsTemplateModal(),
            'confirmSaveAsTemplate': () => deps.confirmSaveAsTemplate(),
            'deleteTemplateById': () => deps.deleteTemplateById(param),
            'openDeleteTemplateModal': () => deps.openDeleteTemplateModal(param),
            'closeDeleteTemplateModal': () => deps.closeDeleteTemplateModal(),
            'confirmDeleteTemplate': () => deps.confirmDeleteTemplate(),
            'openRenameTemplateModal': () => deps.openRenameTemplateModal(param),
            'closeRenameTemplateModal': () => deps.closeRenameTemplateModal(),
            'confirmRenameTemplate': () => deps.confirmRenameTemplate(),

            // Feedback operations
            'addFeedbackItem': () => deps.addFeedbackItem(),
            'deleteFeedbackItem': () => deps.deleteFeedbackItem(parseInt(param)),
            'confirmFeedbackDelete': () => deps.confirmFeedbackDelete(),

            // History operations
            'toggleHistoryEntryInline': () => deps.toggleHistoryEntryInline(parseInt(param)),

            // Formatting
            'formatTaskText': () => deps.formatTaskText(param),
            'insertTaskHeading': () => deps.insertTaskHeading(param),
            'insertTaskDivider': () => deps.insertTaskDivider(),
            'insertTaskChecklist': () => deps.insertTaskChecklist(),
            'insertTaskDateChip': () => deps.insertTaskDateChip(param),
            'formatProjectDescriptionText': () => deps.formatProjectDescriptionText(param),
            'insertProjectDescriptionHeading': () => deps.insertProjectDescriptionHeadingCmd(param),
            'insertProjectDescriptionDivider': () => deps.insertProjectDescriptionDividerCmd(),
            'insertProjectDescriptionChecklist': () => deps.insertProjectDescriptionChecklistCmd(),
            'insertProjectDescriptionDateChip': () => deps.insertProjectDescriptionDateChipCmd(param),

            // Sorting & filtering
            'sortTable': () => deps.sortTable(param),
            'toggleSortMode': () => deps.toggleSortMode(),
            'openMobileSortSheet': () => deps.openMobileSortSheet(),
            'closeMobileSortSheetOnBackdrop': () => deps.closeMobileSortSheet(),
            'applyMobileSort': () => deps.applyMobileSort(),

            // Calendar
            'changeMonth': () => deps.animateCalendarMonthChange(parseInt(param)),
            'goToToday': () => deps.goToToday(),
            'showDayTasks': () => deps.showDayTasks(param),

            // Attachments & tags
            'addAttachment': () => deps.addAttachment(),
            'addFileAttachment': () => deps.addFileAttachment(event),
            'addTag': () => deps.addTag(),
            'removeTag': () => deps.removeTag(param),
            'addProjectTag': () => deps.addProjectTag(),
            'removeProjectTag': () => deps.removeProjectTag(param),
            'addProjectDetailsTag': () => deps.addProjectDetailsTag(param),
            'removeProjectDetailsTag': () => deps.removeProjectDetailsTag(param),
            'removeAttachment': () => { deps.removeAttachment(parseInt(param)); event.preventDefault(); },
            'removeDependency': () => { deps.removeDependency(parseInt(param)); event.preventDefault(); },
            'addTaskLink': () => deps.addTaskLink(),
            'removeTaskLink': () => { deps.removeTaskLink(parseInt(param), param2); event.preventDefault(); },
            'removeTempLink': () => { deps.removeTempLink(parseInt(param)); event.preventDefault(); },
            'openUrlAttachment': () => {
                if (!param) return;
                try {
                    const href = decodeURIComponent(param);
                    window.open(href, '_blank', 'noopener,noreferrer');
                } catch (e) {
                    console.error('Failed to open URL attachment:', e);
                }
            },
            'downloadFileAttachment': () => deps.downloadFileAttachment(param, param2, target.dataset.param3),
            'viewFile': () => deps.viewFile(param, param2, target.dataset.param3, target.dataset.param4),
            'viewImageLegacy': () => deps.viewImageLegacy(param, param2, target.dataset.param3),
            'viewFeedbackScreenshot': async () => {
                if (!param) return;
                try {
                    const decoded = decodeURIComponent(param);
                    let src = decoded;
                    
                    // If it's a KV reference ID (starts with "screenshot:"), fetch from API
                    if (decoded.startsWith('screenshot:')) {
                        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
                        if (!token) {
                            deps.showErrorNotification && deps.showErrorNotification('Not authenticated');
                            return;
                        }
                        
                        const response = await fetch(`/api/feedback-screenshot?id=${encodeURIComponent(decoded)}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });
                        
                        if (response.ok) {
                            const result = await response.json();
                            src = result.data;
                        } else {
                            throw new Error('Failed to load screenshot');
                        }
                    }
                    
                    const title = 'Feedback Screenshot';
                    deps.viewImageLegacy(src, title);
                } catch (e) {
                    console.error('Failed to open feedback screenshot', e);
                    deps.showErrorNotification && deps.showErrorNotification(deps.t('error.openScreenshotFailed'));
                }
            },

            // Navigation
            'backToProjects': () => deps.backToProjects(),
            'backToArchivedProjects': () => deps.backToArchivedProjects(),
            'showAllActivity': () => deps.showAllActivity(),
            'backToDashboard': () => deps.backToDashboard(),
            'backToCalendar': () => deps.backToCalendar(),
            'openUpdatesFromNotification': () => deps.openUpdatesFromNotification(),
            'openDueTodayFromNotification': () => deps.openDueTodayFromNotification(),

            // Other
            'dismissKanbanTip': () => deps.dismissKanbanTip(),
            'confirmDiscardChanges': () => deps.confirmDiscardChanges(),
            'closeReviewStatusConfirmModal': () => deps.closeReviewStatusConfirmModal(),
            'confirmDisableReviewStatus': () => deps.confirmDisableReviewStatus(),
            'closeCalendarCreateModal': () => deps.closeCalendarCreateModal(),
            'confirmCreateTask': () => deps.confirmCreateTask(),
            'addTaskFromDayItemsModal': () => deps.addTaskFromDayItemsModal(),
            'signOut': () => deps.signOut(),
            'exportDashboardData': () => deps.exportDashboardData(),
            'closeExportDataModal': () => deps.closeExportDataModal(),
            'confirmExportData': () => deps.confirmExportData(),
            'exportTasksToExcel': () => deps.exportTasksToExcel(),
            'openImportDataModal': () => deps.openImportDataModal(),
            'closeImportDataModal': () => deps.closeImportDataModal(),
            'confirmImportData': () => deps.confirmImportData(),
            'generateReport': () => deps.generateReport(),
            'generateSummaryReport': () => deps.generateSummaryReport(),
            'closeSummaryReport': () => deps.closeSummaryReport(),
            'refreshSummaryReport': () => deps.refreshSummaryReport(),
            'saveSummaryReportToDocs': () => deps.saveSummaryReportToDocs(),
            'printSummaryReport': () => deps.printSummaryReport(),
            'showStatusInfoModal': () => {
                event.stopPropagation();
                deps.showStatusInfoModal();
            },

            // Special case: stopPropagation
            'stopPropagation': () => event.stopPropagation(),

            // Formerly inline on*= handlers (blocked by the enforced CSP)
            'toggleKanbanBacklog': () => deps.toggleKanbanBacklog(),
            'toggleKanbanProjects': () => deps.toggleKanbanProjects(),
            'toggleKanbanTags': () => deps.toggleKanbanTags(),
            'toggleKanbanNoDate': () => deps.toggleKanbanNoDate(),
            'closeDocsShareModal': () => deps.closeDocsShareModal(),
            'closeCropModal': () => deps.closeCropModal(),
            'applyCrop': () => deps.applyCrop(),
            'changeFeedbackPage': () => deps.changeFeedbackPage(param, parseInt(param2, 10)),
            'openReviewTasksInNewTab': () => deps.openReviewTasksInNewTab(),
            'removeParentElement': () => target.parentElement?.remove(),
            'removeElementById': () => document.getElementById(param)?.remove(),

            // Special case: close modal only if backdrop is clicked
            'closeModalOnBackdrop': () => {
                const startedOnBackdrop = backdropPointerStarts.has(target);
                backdropPointerStarts.delete(target);

                if (event.target === target && startedOnBackdrop) {
                    // Modals with their own cleanup name it via data-close-action.
                    const closeAction = target.dataset.closeAction;
                    if (closeAction && actions[closeAction]) actions[closeAction]();
                    else deps.closeModal(param);
                }
            },

            // Combined actions
            'closeDayItemsAndOpenTask': () => {
                deps.closeDayItemsModal();
                deps.openTaskDetails(parseInt(param));
            },
            'closeDayItemsAndShowProject': () => {
                deps.closeDayItemsModal();
                deps.showProjectDetails(parseInt(param), 'calendar', {
                    month: deps.getCurrentMonth(),
                    year: deps.getCurrentYear()
                });
            },
            'deleteFeedbackItemWithStop': () => {
                deps.deleteFeedbackItem(String(param)); // D1 IDs are TEXT strings
                event.stopPropagation();
            },
            
            // Mass Edit actions
            'toggleTaskSelection': () => {
                event.stopPropagation();
                deps.toggleTaskSelection(parseInt(param), event);
            },
            'closeMassEditPopover': () => deps.closeMassEditPopover(),
            'applyMassEdit': () => {
                const btn = event.target.closest('[data-action="applyMassEdit"]');
                if (btn && (btn.disabled || btn.getAttribute('disabled') !== null)) return;
                deps.queueMassEditChange();
            },
            'applyAllMassEditChanges': () => deps.applyAllMassEditChanges(),
            'closeMassEditConfirm': () => deps.closeMassEditConfirm(),
            'applyMassEditConfirmed': () => deps.applyMassEditConfirmed(),
            'closeMassDeleteConfirmModal': () => deps.closeMassDeleteConfirmModal(),
            'confirmMassDelete': () => deps.confirmMassDelete(),

            // Archive / Restore (tasks)
            'archiveTask':           () => deps.archiveTask(parseInt(param)),
            'restoreTask':           () => deps.restoreTask(parseInt(param)),
            'restoreArchivedTask':   () => deps.restoreArchivedTask(parseInt(param)),
            'toggleArchivedAutomatedFilter': () => deps.toggleArchivedAutomatedFilter(),
            'toggleArchivedTaskSelection': () => {
                event.stopPropagation();
                deps.toggleArchivedTaskSelection(parseInt(param));
            },
            'toggleAllArchivedTasks': () => {
                event.stopPropagation();
                deps.toggleAllArchivedTasks();
            },
            'clearArchivedTaskSelection': () => deps.clearArchivedTaskSelection(),
            'bulkRestoreArchivedTasks': () => deps.bulkRestoreArchivedTasks(),
            'bulkArchiveTasks':      () => deps.bulkArchiveTasks(),
            'bulkRestoreTasks':      () => deps.bulkRestoreTasks(),

            // Archive / Restore (projects)
            'openProjectArchiveModal':    () => deps.openProjectArchiveModal(parseInt(param)),
            'closeProjectArchiveModal':   () => deps.closeProjectArchiveModal(),
            'confirmProjectArchive':      () => deps.confirmProjectArchive(),
            'openProjectRestoreModal':    () => deps.openProjectRestoreModal(parseInt(param)),
            'closeProjectRestoreModal':   () => deps.closeProjectRestoreModal(),
            'confirmProjectRestore':      () => deps.confirmProjectRestore(),
            'toggleArchivedProjectSelection': () => {
                event.stopPropagation();
                deps.toggleArchivedProjectSelection(parseInt(param));
            },
            'toggleAllArchivedProjects': () => {
                event.stopPropagation();
                deps.toggleAllArchivedProjects();
            },
            'clearArchivedProjectSelection': () => deps.clearArchivedProjectSelection(),
            'bulkRestoreArchivedProjects':   () => deps.bulkRestoreArchivedProjects(),
            'closeBulkProjectRestoreModal':  () => deps.closeBulkProjectRestoreModal(),
            'confirmBulkProjectRestore':     () => deps.confirmBulkProjectRestore(),
        };

        // Field diagnostics: record every dispatched action in the on-device
        // overlay (no-op unless the user opted in with ?diag=1), so field
        // reports show which handler ran — not just which element was tapped.
        if (typeof window !== 'undefined' && typeof window.__diag === 'function') {
            window.__diag('ACTION ' + action + (param ? ' param=' + param : '') + (param2 ? ' param2=' + param2 : ''));
        }

        // Execute the action if it exists
        if (actions[action]) {
            try {
                actions[action]();
            } catch (err) {
                // A thrown action (e.g. modal-open setup) must not silently abort
                // and leave the UI dead. Surface the REAL error — caught errors are
                // not subject to the cross-origin "Script error." masking that hides
                // uncaught module exceptions on iOS Chrome/Brave.
                const detail = err && (err.stack || err.message) ? (err.message + ' | ' + (err.stack || '')) : String(err);
                if (typeof window !== 'undefined' && typeof window.__diag === 'function') {
                    window.__diag('ACTION "' + action + '" threw: ' + detail);
                }
                console.error('[action error]', action, err);
            }
        } else {
            if (typeof window !== 'undefined' && typeof window.__diag === 'function') {
                window.__diag('ACTION "' + action + '" has NO handler');
            }
            console.warn(`No handler found for action: ${action}`);
        }
    });
}
